document.documentElement.classList.remove('no-js');

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const supportsIntersectionObserver = 'IntersectionObserver' in window;
const state = {
  scrollFrame: 0,
  cartTrigger: null,
  menuTrigger: null,
  activeLayer: null,
  inertElements: [],
  scrollLocks: new Set(),
  originalBodyOverflow: '',
  originalBodyPaddingRight: ''
};

if (reducedMotion || !supportsIntersectionObserver) {
  document.documentElement.classList.add('motion-safe-fallback');
}

const createObserver = (callback, options) => supportsIntersectionObserver
  ? new IntersectionObserver(callback, options)
  : { observe() {}, unobserve() {}, disconnect() {} };

const scrollProgress = document.createElement('div');
scrollProgress.className = 'scroll-progress';
scrollProgress.setAttribute('aria-hidden', 'true');
document.body.append(scrollProgress);

const titleObserver = createObserver(entries => {
  entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    entry.target.classList.add('title-visible');
    titleObserver.unobserve(entry.target);
  });
}, { threshold: 0.35, rootMargin: '0px 0px -8% 0px' });

const revealObserver = createObserver(entries => {
  entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    entry.target.classList.add('visible');
    revealObserver.unobserve(entry.target);
  });
}, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

const sectionObserver = createObserver(entries => {
  entries.forEach(entry => {
    entry.target.classList.toggle('in-view', entry.isIntersecting);
    if (!entry.isIntersecting || !entry.target.id) return;
    document.querySelectorAll('.nav-links a').forEach(link => {
      const url = new URL(link.href, window.location.origin);
      link.classList.toggle('active', url.hash === `#${entry.target.id}`);
    });
  });
}, { threshold: 0.2, rootMargin: '-20% 0px -55% 0px' });

const ambientObserver = createObserver(entries => {
  entries.forEach(entry => {
    entry.target.classList.toggle('motion-active', entry.isIntersecting);
  });
}, { threshold: 0.08, rootMargin: '12% 0px 12% 0px' });

const actionVideoObserver = createObserver(entries => {
  entries.forEach(entry => {
    const wrapper = entry.target;
    const video = wrapper.querySelector('[data-action-video]');
    if (!video) return;
    const section = wrapper.closest('[data-action-video-section]');
    const inView = entry.intersectionRatio >= 0.35;
    wrapper.dataset.actionVideoInView = String(inView);
    if (entry.isIntersecting) section?.classList.add('is-visible');
    if (inView) {
      if (!reducedMotion && wrapper.dataset.actionVideoAutoplay === 'true' && wrapper.dataset.actionVideoManualPaused !== 'true') {
        playActionVideo(wrapper);
      }
    } else {
      if (wrapper.dataset.actionVideoPlayPending !== 'true') pauseActionVideo(wrapper);
    }
  });
}, { threshold: [0, 0.35, 0.6], rootMargin: '0px 0px -8% 0px' });

function initTitles(scope = document) {
  scope.querySelectorAll('.animated-title').forEach(title => {
    if (title.dataset.titleReady) return;
    title.dataset.titleReady = 'true';
    titleObserver.observe(title);
  });
}

function initAmbientMotion(scope = document) {
  if (reducedMotion) return;
  scope.querySelectorAll('.hero, .hero-product, .tech-orbit').forEach(element => {
    if (element.dataset.ambientReady) return;
    element.dataset.ambientReady = 'true';
    ambientObserver.observe(element);
  });
}

function initReveals(scope = document) {
  scope.querySelectorAll('.reveal, .reveal-right').forEach(element => {
    if (element.dataset.revealReady) return;
    element.dataset.revealReady = 'true';
    const siblings = [...element.parentElement.querySelectorAll(':scope > .reveal, :scope > .reveal-right')];
    const localIndex = Math.max(0, siblings.indexOf(element));
    element.style.transitionDelay = `${Math.min(localIndex, 4) * 85}ms`;
    revealObserver.observe(element);
  });
  scope.querySelectorAll('main .section').forEach(section => sectionObserver.observe(section));
}

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function getFocusable(container) {
  return [...container.querySelectorAll(focusableSelector)].filter(element => {
    if (element.closest('[aria-hidden="true"], [inert]')) return false;
    return element.offsetWidth > 0 || element.offsetHeight > 0 || element === document.activeElement;
  });
}

function lockPageScroll(key) {
  if (state.scrollLocks.has(key)) return;
  if (state.scrollLocks.size === 0) {
    state.originalBodyOverflow = document.body.style.overflow;
    state.originalBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
  state.scrollLocks.add(key);
}

function unlockPageScroll(key) {
  state.scrollLocks.delete(key);
  if (state.scrollLocks.size > 0) return;
  document.body.style.overflow = state.originalBodyOverflow;
  document.body.style.paddingRight = state.originalBodyPaddingRight;
}

function clearBackgroundInert() {
  state.inertElements.forEach(element => { element.inert = false; });
  state.inertElements = [];
}

function setBackgroundInert(activeRoots) {
  clearBackgroundInert();
  const roots = activeRoots.filter(Boolean);
  [...document.body.children].forEach(element => {
    const keepActive = roots.some(root => element === root || element.contains(root));
    if (keepActive) return;
    element.inert = true;
    state.inertElements.push(element);
  });
}

function activateLayer({ type, container, inertRoot, trigger, focusTarget, bodyClass, close }) {
  if (!container) return;
  state.activeLayer = { type, container, trigger, close };
  if (bodyClass) document.body.classList.add(bodyClass);
  lockPageScroll(type);
  setBackgroundInert([inertRoot || container]);
  const target = focusTarget || getFocusable(container)[0] || container;
  window.setTimeout(() => target?.focus?.({ preventScroll: true }), reducedMotion ? 0 : 120);
}

function deactivateLayer(type, { restoreFocus = true, bodyClass } = {}) {
  if (state.activeLayer?.type === type) {
    const trigger = state.activeLayer.trigger;
    state.activeLayer = null;
    clearBackgroundInert();
    if (restoreFocus && trigger instanceof HTMLElement && document.contains(trigger)) {
      trigger.focus({ preventScroll: true });
    }
  }
  if (bodyClass) document.body.classList.remove(bodyClass);
  unlockPageScroll(type);
}

function getOpenMenu() {
  const button = document.querySelector('[data-menu-button].open');
  const header = button?.closest('[data-header]');
  const menu = header?.querySelector('[data-mobile-menu].open');
  return button && menu ? { header, button, menu } : null;
}

function closeMobileMenu({ restoreFocus = true } = {}) {
  const openMenu = getOpenMenu();
  if (!openMenu) return;
  const { button, menu } = openMenu;
  button.classList.remove('open');
  menu.classList.remove('open');
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-label', button.dataset.openLabel);
  menu.setAttribute('aria-hidden', 'true');
  menu.inert = true;
  deactivateLayer('menu', { restoreFocus, bodyClass: 'menu-open' });
}

function initMenu(scope = document) {
  scope.querySelectorAll('[data-header]').forEach(header => {
    if (header.dataset.menuReady) return;
    header.dataset.menuReady = 'true';
    const button = header.querySelector('[data-menu-button]');
    const menu = header.querySelector('[data-mobile-menu]');
    if (!button || !menu) return;
    button.addEventListener('click', () => {
      const shouldOpen = !button.classList.contains('open');
      if (!shouldOpen) {
        closeMobileMenu();
        return;
      }
      closeCart({ restoreFocus: false });
      state.menuTrigger = button;
      button.classList.add('open');
      menu.classList.add('open');
      button.setAttribute('aria-expanded', 'true');
      button.setAttribute('aria-label', button.dataset.closeLabel);
      menu.setAttribute('aria-hidden', 'false');
      menu.inert = false;
      activateLayer({
        type: 'menu',
        container: menu,
        inertRoot: header,
        trigger: button,
        focusTarget: getFocusable(menu)[0],
        bodyClass: 'menu-open',
        close: () => closeMobileMenu()
      });
    });
    menu.querySelectorAll('a').forEach(link => link.addEventListener('click', () => closeMobileMenu({ restoreFocus: false })));
  });
}

function updateActionVideoToggle(wrapper) {
  const video = wrapper.querySelector('[data-action-video]');
  const toggle = wrapper.querySelector('[data-action-video-toggle]');
  if (!video || !toggle) return;
  const isPlaying = !video.paused && !video.ended;
  toggle.classList.toggle('is-playing', isPlaying);
  toggle.setAttribute('aria-pressed', String(isPlaying));
  toggle.setAttribute('aria-label', isPlaying ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
  toggle.setAttribute('title', isPlaying ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
}

async function playActionVideo(wrapper, { manual = false } = {}) {
  const video = wrapper.querySelector('[data-action-video]');
  const toggle = wrapper.querySelector('[data-action-video-toggle]');
  if (!video || wrapper.dataset.actionVideoPlayPending === 'true') return;
  video.muted = true;
  if (manual) wrapper.dataset.actionVideoManualPaused = 'false';
  wrapper.dataset.actionVideoPlayPending = 'true';
  toggle?.setAttribute('aria-busy', 'true');
  try {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === 'function') await playPromise;
    updateActionVideoToggle(wrapper);
  } catch (error) {
    console.warn('ELYMATIC video playback was prevented.', error);
    wrapper.dataset.actionVideoManualPaused = 'true';
    updateActionVideoToggle(wrapper);
  } finally {
    delete wrapper.dataset.actionVideoPlayPending;
    toggle?.removeAttribute('aria-busy');
  }
}

function pauseActionVideo(wrapper, { manual = false } = {}) {
  const video = wrapper.querySelector('[data-action-video]');
  if (!video) return;
  if (manual) wrapper.dataset.actionVideoManualPaused = 'true';
  delete wrapper.dataset.actionVideoPlayPending;
  video.pause();
  updateActionVideoToggle(wrapper);
}

function initActionVideos(scope = document) {
  scope.querySelectorAll('[data-action-video-section]').forEach(section => {
    section.classList.add('is-visible');
  });

  scope.querySelectorAll('[data-action-video-wrapper]').forEach(wrapper => {
    if (wrapper.dataset.actionVideoReady === 'true') return;
    const video = wrapper.querySelector('[data-action-video]');
    const toggle = wrapper.querySelector('[data-action-video-toggle]');
    const section = wrapper.closest('[data-action-video-section]');
    section?.classList.add('is-visible');

    if (!video || !toggle) return;
    wrapper.dataset.actionVideoReady = 'true';
    wrapper.dataset.actionVideoManualPaused = 'false';
    wrapper.dataset.actionVideoInView = 'false';
    video.muted = true;
    video.autoplay = false;
    video.controls = false;
    video.preload = 'metadata';
    video.setAttribute('playsinline', '');
    updateActionVideoToggle(wrapper);

    const onToggleClick = () => {
      if (video.paused || video.ended) {
        playActionVideo(wrapper, { manual: true });
      } else {
        pauseActionVideo(wrapper, { manual: true });
      }
    };
    const onPlay = () => {
      if (wrapper.dataset.actionVideoPlayPending === 'true') return;
      updateActionVideoToggle(wrapper);
    };
    const onPause = () => updateActionVideoToggle(wrapper);

    toggle.addEventListener('click', onToggleClick);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    if (supportsIntersectionObserver) actionVideoObserver.observe(wrapper);

    wrapper.actionVideoCleanup = () => {
      actionVideoObserver.unobserve(wrapper);
      toggle.removeEventListener('click', onToggleClick);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      pauseActionVideo(wrapper);
      delete wrapper.dataset.actionVideoReady;
    };
  });

  if (!state.actionVideoVisibilityReady) {
    state.actionVideoVisibilityReady = true;
    document.addEventListener('visibilitychange', () => {
      document.querySelectorAll('[data-action-video-wrapper][data-action-video-ready="true"]').forEach(wrapper => {
        if (document.hidden) {
          pauseActionVideo(wrapper);
        } else if (!reducedMotion && wrapper.dataset.actionVideoAutoplay === 'true' && wrapper.dataset.actionVideoInView === 'true' && wrapper.dataset.actionVideoManualPaused !== 'true') {
          playActionVideo(wrapper);
        }
      });
    });
  }
}

document.addEventListener('shopify:section:unload', event => {
  event.target.querySelectorAll('[data-action-video-wrapper]').forEach(wrapper => {
    wrapper.actionVideoCleanup?.();
  });
});

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'EUR';
  return new Intl.NumberFormat(document.documentElement.lang || 'es', {
    style: 'currency',
    currency
  }).format(cents / 100);
}

function initProductSections(scope = document) {
  scope.querySelectorAll('[data-product-section]').forEach(section => {
    if (section.dataset.productReady) return;
    section.dataset.productReady = 'true';
    const form = section.querySelector('.product-form');
    const variantsNode = section.querySelector('[data-product-variants]');
    if (!form || !variantsNode) return;
    const variants = JSON.parse(variantsNode.textContent);
    const variantMediaNode = section.querySelector('[data-product-variant-media]');
    const variantMedia = variantMediaNode ? JSON.parse(variantMediaNode.textContent) : {};
    const selectors = [...form.querySelectorAll('[data-option-selector]')];
    const variantInput = form.querySelector('[data-variant-id]');
    const addButton = form.querySelector('[data-add-to-cart]');
    const addButtonText = addButton?.querySelector('span');
    const price = section.querySelector('[data-product-price]');
    const compare = section.querySelector('[data-compare-price]');
    const discount = section.querySelector('[data-discount-badge]');
    const purchaseImage = section.querySelector('[data-product-media-image]');
    const sticky = section.querySelector('[data-sticky-buy]');
    const stickyPrice = section.querySelector('[data-sticky-price]');
    const stickyCta = section.querySelector('[data-sticky-cta]');
    if (!variantInput || !addButton || !addButtonText || !price) return;

    const setButtonState = (variant, valid = true) => {
      const available = Boolean(valid && variant?.available);
      const disabledText = valid ? window.ELYMATIC.strings.soldOut : window.ELYMATIC.strings.unavailable;
      addButton.disabled = !available;
      addButton.setAttribute('aria-disabled', String(!available));
      addButtonText.textContent = available ? window.ELYMATIC.strings.addToCart : disabledText;
      if (!stickyCta) return;
      stickyCta.disabled = !available;
      stickyCta.classList.toggle('is-disabled', !available);
      stickyCta.setAttribute('aria-disabled', String(!available));
    };

    const findVariantFromOptions = () => {
      if (!selectors.length) {
        const currentId = Number(variantInput.value);
        return variants.find(candidate => Number(candidate.id) === currentId) || variants[0] || null;
      }
      const selectedOptions = selectors.map(select => select.value);
      return variants.find(candidate => (
        candidate.options.length === selectedOptions.length &&
        candidate.options.every((option, index) => option === selectedOptions[index])
      )) || null;
    };

    const updatePrice = variant => {
      price.textContent = formatMoney(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      compare?.classList.toggle('hidden', !onSale);
      discount?.classList.toggle('hidden', !onSale);
      if (compare) compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
      if (discount) {
        discount.textContent = onSale
          ? `Ahorra ${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)}%`
          : '';
      }
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    };

    const updateImage = variant => {
      const media = variantMedia[String(variant.id)]?.image;
      if (!purchaseImage || !media?.src) return;
      purchaseImage.sizes = media.sizes || purchaseImage.sizes;
      if (media.srcset) purchaseImage.srcset = media.srcset;
      if (media.width) purchaseImage.width = media.width;
      if (media.height) purchaseImage.height = media.height;
      purchaseImage.alt = media.alt || purchaseImage.alt;
      purchaseImage.src = media.src;
    };

    const updateVariant = () => {
      const variant = findVariantFromOptions();
      if (!variant) {
        setButtonState(null, false);
        return;
      }
      variantInput.value = variant.id;
      updatePrice(variant);
      updateImage(variant);
      setButtonState(variant, true);
    };
    selectors.forEach(select => select.addEventListener('change', updateVariant));
    stickyCta?.addEventListener('click', () => {
      const variant = findVariantFromOptions();
      if (!variant || !variant.available) {
        setButtonState(variant, Boolean(variant));
        const target = selectors.find(select => !select.value) || selectors[0] || addButton;
        target?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
        window.setTimeout(() => target?.focus({ preventScroll: true }), reducedMotion ? 0 : 320);
        return;
      }
      variantInput.value = variant.id;
      submitProductForm(form, stickyCta);
    });
    updateVariant();
  });
}

function initQuantityControls(scope = document) {
  scope.querySelectorAll('[data-quantity-selector]').forEach(selector => {
    if (selector.dataset.quantityReady) return;
    selector.dataset.quantityReady = 'true';
    const input = selector.querySelector('[data-quantity-input]');
    const minus = selector.querySelector('[data-quantity-minus]');
    const plus = selector.querySelector('[data-quantity-plus]');
    if (!input || !minus || !plus) return;
    minus.addEventListener('click', () => { input.value = Math.max(1, Number(input.value) - 1); });
    plus.addEventListener('click', () => { input.value = Number(input.value) + 1; });
    input.addEventListener('change', () => { input.value = Math.max(1, Number(input.value) || 1); });
  });
}

function initProductGalleries(scope = document) {
  scope.querySelectorAll('[data-product-gallery]').forEach(gallery => {
    if (gallery.dataset.galleryReady) return;
    gallery.dataset.galleryReady = 'true';
    const dialog = gallery.querySelector('[data-gallery-dialog]');
    const dialogImage = gallery.querySelector('[data-gallery-dialog-image]');
    if (!dialog || !dialogImage) return;

    const closeDialog = () => {
      if (dialog.open) dialog.close();
      dialogImage.removeAttribute('src');
    };

    gallery.querySelectorAll('[data-gallery-open]').forEach(trigger => {
      const preload = () => {
        if (trigger.dataset.preloaded) return;
        const image = new Image();
        image.src = trigger.dataset.gallerySrc;
        trigger.dataset.preloaded = 'true';
      };
      trigger.addEventListener('pointerenter', preload, { once: true });
      trigger.addEventListener('focus', preload, { once: true });
      trigger.addEventListener('click', () => {
        dialogImage.src = trigger.dataset.gallerySrc;
        dialogImage.alt = trigger.dataset.galleryAlt || '';
        dialog.showModal();
        dialog.querySelector('[data-gallery-close]')?.focus();
      });
    });

    dialog.querySelector('[data-gallery-close]')?.addEventListener('click', closeDialog);
    dialog.addEventListener('click', event => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('close', () => dialogImage.removeAttribute('src'));
  });
}

function getCartStrings() {
  const source = document.querySelector('[data-cart-drawer-section]')?.dataset || {};
  return {
    addSuccess: source.cartAddSuccess || 'Producto añadido al carrito.',
    addError: source.cartAddError || 'No se pudo añadir el producto. Inténtalo de nuevo.',
    updateSuccess: source.cartUpdateSuccess || 'Carrito actualizado.',
    updateError: source.cartUpdateError || 'No se pudo actualizar el carrito. Inténtalo de nuevo.',
    refreshError: source.cartRefreshError || 'No se pudo actualizar el carrito.',
    invalidResponse: source.cartInvalidResponse || 'La respuesta del carrito no es válida.'
  };
}

function announceCart(message) {
  const status = document.querySelector('[data-cart-drawer-status]');
  if (status) status.textContent = message;
}

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach(element => {
    element.textContent = String(count);
    element.classList.toggle('is-empty', count === 0);
  });
  document.querySelectorAll('[data-cart-trigger]').forEach(trigger => {
    const template = trigger.dataset.cartLabelTemplate;
    if (template) trigger.setAttribute('aria-label', template.replace('[count]', String(count)));
  });
}

async function refreshCartDrawer() {
  const strings = getCartStrings();
  const url = new URL(window.location.href);
  url.searchParams.set('sections', 'cart-drawer');
  const currentSection = document.querySelector('[data-cart-drawer-section]');
  const wasOpen = Boolean(document.querySelector('[data-cart-drawer].open'));
  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  });
  if (!response.ok) throw new Error(`${strings.refreshError} (${response.status})`);
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) throw new Error(strings.invalidResponse);
  let sections;
  try {
    sections = await response.json();
  } catch (error) {
    throw new Error(strings.invalidResponse);
  }
  const html = sections?.['cart-drawer'];
  if (typeof html !== 'string' || !html.trim()) throw new Error(strings.invalidResponse);
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const nextSection = parsed.querySelector('[data-cart-drawer-section]');
  if (!nextSection || !currentSection) throw new Error(strings.invalidResponse);
  currentSection.replaceWith(nextSection);
  if (wasOpen) {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');
    drawer?.classList.add('open');
    overlay?.classList.add('open');
    drawer?.setAttribute('aria-hidden', 'false');
    if (drawer) drawer.inert = false;
    overlay?.setAttribute('aria-hidden', 'false');
    activateLayer({
      type: 'cart',
      container: drawer,
      inertRoot: nextSection,
      trigger: state.cartTrigger,
      focusTarget: drawer?.querySelector('[data-cart-close]') || getFocusable(drawer || nextSection)[0],
      bodyClass: 'cart-open',
      close: () => closeCart()
    });
  }
  return nextSection;
}

function openCart(trigger) {
  closeMobileMenu({ restoreFocus: false });
  state.cartTrigger = trigger || document.activeElement;
  const drawer = document.querySelector('[data-cart-drawer]');
  const overlay = document.querySelector('[data-cart-overlay]');
  const section = drawer?.closest('[data-cart-drawer-section]');
  if (!drawer || !overlay) return;
  drawer.classList.add('open');
  overlay.classList.add('open');
  drawer.setAttribute('aria-hidden', 'false');
  drawer.inert = false;
  overlay.setAttribute('aria-hidden', 'false');
  activateLayer({
    type: 'cart',
    container: drawer,
    inertRoot: section,
    trigger: state.cartTrigger,
    focusTarget: drawer.querySelector('[data-cart-close]') || getFocusable(drawer)[0],
    bodyClass: 'cart-open',
    close: () => closeCart()
  });
}

function closeCart({ restoreFocus = true } = {}) {
  const drawer = document.querySelector('[data-cart-drawer]');
  const overlay = document.querySelector('[data-cart-overlay]');
  if (!drawer || !overlay) return;
  drawer.classList.remove('open');
  overlay.classList.remove('open');
  drawer.setAttribute('aria-hidden', 'true');
  drawer.inert = true;
  overlay.setAttribute('aria-hidden', 'true');
  deactivateLayer('cart', { restoreFocus, bodyClass: 'cart-open' });
}

async function submitProductForm(form, trigger) {
  if (form.dataset.submitting === 'true') return;
  const strings = getCartStrings();
  const button = form.querySelector('[data-add-to-cart]');
  const section = form.closest('[data-product-section]');
  const stickyCta = section?.querySelector('[data-sticky-cta]');
  const variantInput = form.querySelector('[data-variant-id]');
  const variantsNode = section?.querySelector('[data-product-variants]');
  const variants = variantsNode ? JSON.parse(variantsNode.textContent) : [];
  const status = form.querySelector('[data-product-form-status]');
  if (!button || button.disabled || trigger?.disabled) return;
  form.dataset.submitting = 'true';
  button.disabled = true;
  button.classList.add('loading');
  if (stickyCta) {
    stickyCta.disabled = true;
    stickyCta.classList.add('loading');
    stickyCta.setAttribute('aria-busy', 'true');
  }
  if (status) status.textContent = '';
  try {
    const response = await fetch(`${window.ELYMATIC.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    });
    if (!response.ok) throw new Error(strings.addError);
    const cart = await response.json();
    await refreshCartDrawer();
    updateCartCount(cart.item_count || 0);
    openCart(trigger || button);
    if (status) status.textContent = strings.addSuccess;
    announceCart(strings.addSuccess);
  } catch (error) {
    console.error(error);
    if (status) status.textContent = strings.addError;
    announceCart(strings.addError);
  } finally {
    delete form.dataset.submitting;
    const selectedVariant = variants.find(variant => String(variant.id) === String(variantInput?.value));
    const available = Boolean(selectedVariant?.available);
    button.disabled = !available;
    button.classList.remove('loading');
    if (stickyCta) {
      stickyCta.disabled = !available;
      stickyCta.classList.toggle('is-disabled', !available);
      stickyCta.setAttribute('aria-disabled', String(!available));
      stickyCta.classList.remove('loading');
      stickyCta.removeAttribute('aria-busy');
    }
  }
}

document.addEventListener('submit', async event => {
  const form = event.target.closest('.product-form');
  if (!form || !event.submitter?.matches('[data-add-to-cart]')) return;
  event.preventDefault();
  await submitProductForm(form, event.submitter);
});

async function updateCartLine(lineItem, quantity) {
  if (!lineItem || lineItem.dataset.updating === 'true') return;
  const strings = getCartStrings();
  const line = Number(lineItem.dataset.cartLine);
  if (!line) return;
  lineItem.dataset.updating = 'true';
  lineItem.classList.add('is-updating');
  lineItem.setAttribute('aria-busy', 'true');
  lineItem.querySelectorAll('button, a').forEach(control => {
    control.setAttribute('aria-disabled', 'true');
    if (control.tagName === 'BUTTON') control.disabled = true;
  });
  try {
    const response = await fetch(`${window.ELYMATIC.routes.cartChange}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ line, quantity: Math.max(0, quantity) })
    });
    if (!response.ok) throw new Error(strings.updateError);
    const cart = await response.json();
    await refreshCartDrawer();
    updateCartCount(cart.item_count || 0);
    announceCart(strings.updateSuccess);
  } catch (error) {
    console.error(error);
    announceCart(strings.updateError);
    lineItem.dataset.updating = 'false';
    lineItem.classList.remove('is-updating');
    lineItem.removeAttribute('aria-busy');
    lineItem.querySelectorAll('button, a').forEach(control => {
      control.setAttribute('aria-disabled', 'false');
      if (control.tagName === 'BUTTON') control.disabled = false;
    });
  }
}

document.addEventListener('click', event => {
  const cartTrigger = event.target.closest('[data-cart-trigger]');
  if (cartTrigger) {
    event.preventDefault();
    openCart(cartTrigger);
    return;
  }
  const quantityButton = event.target.closest('[data-cart-quantity-minus], [data-cart-quantity-plus]');
  if (quantityButton) {
    event.preventDefault();
    const lineItem = quantityButton.closest('[data-cart-line-item]');
    const currentQuantity = Number(lineItem?.dataset.cartLineQuantity || 0);
    const nextQuantity = quantityButton.matches('[data-cart-quantity-plus]') ? currentQuantity + 1 : currentQuantity - 1;
    updateCartLine(lineItem, nextQuantity);
    return;
  }
  const removeLink = event.target.closest('[data-cart-remove]');
  if (removeLink) {
    event.preventDefault();
    updateCartLine(removeLink.closest('[data-cart-line-item]'), 0);
    return;
  }
  if (event.target.closest('[data-cart-close]') || event.target.matches('[data-cart-overlay]')) closeCart();
  if (event.target.closest('.back-top')) window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
});

document.addEventListener('keydown', event => {
  const layer = state.activeLayer;
  if (!layer?.container) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    layer.close?.();
    return;
  }
  if (event.key !== 'Tab') return;
  const focusable = getFocusable(layer.container);
  if (!focusable.length) {
    event.preventDefault();
    layer.container.focus?.({ preventScroll: true });
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

function renderScroll() {
  state.scrollFrame = 0;
  const nav = document.querySelector('#navbar');
  const heroImage = document.querySelector('.hero-product img');
  const finalCta = document.querySelector('.final-cta');
  const scrollY = window.scrollY;
  const viewportHeight = window.innerHeight;
  const maxScroll = Math.max(1, document.documentElement.scrollHeight - viewportHeight);
  nav?.classList.toggle('scrolled', scrollY > 40);
  scrollProgress.style.transform = `scaleX(${Math.min(1, scrollY / maxScroll)})`;

  if (reducedMotion) return;
  if (heroImage) {
    const heroProgress = Math.min(1, Math.max(0, scrollY / viewportHeight));
    heroImage.style.setProperty('--hero-scale', (1.025 + heroProgress * 0.025).toFixed(4));
  }
  if (finalCta) {
    const rect = finalCta.getBoundingClientRect();
    const progress = Math.min(1, Math.max(0, 1 - rect.top / viewportHeight));
    const rings = finalCta.querySelector('.cta-rings');
    rings?.style.setProperty('--cta-y', `${((1 - progress) * 24).toFixed(2)}px`);
    rings?.style.setProperty('--cta-scale', (0.94 + progress * 0.06).toFixed(4));
    rings?.style.setProperty('--cta-opacity', (0.38 + progress * 0.62).toFixed(3));
  }
}

function requestScrollRender() {
  if (!state.scrollFrame) state.scrollFrame = requestAnimationFrame(renderScroll);
}

window.addEventListener('scroll', requestScrollRender, { passive: true });
window.addEventListener('resize', requestScrollRender, { passive: true });
function initAll(scope = document) {
  initTitles(scope);
  initAmbientMotion(scope);
  initReveals(scope);
  initMenu(scope);
  initActionVideos(scope);
  initProductSections(scope);
  initQuantityControls(scope);
  initProductGalleries(scope);
  renderScroll();
}

initAll();
document.addEventListener('shopify:section:load', event => initAll(event.target));
